import { jwtDecode } from "jwt-decode";
import { CustomJwtPayload } from "types/authTypes";
import { refreshTokenIfNeeded } from "../api-communication/RestApiCli";

interface TokenRefreshResponse {
  tokenRefreshed: boolean;
  expiryTime: number;
  timeUntilExpiry: number;
  message: string;
  access_token?: string;
}

interface ApiResponse<T> {
  data: T;
  status?: number;
  statusText?: string;
  [key: string]: any;
}
export class SecureTokenManager {
  private static readonly TOKEN_KEY = "token";

  static storeTokens(accessToken: string): void {
    try {
      localStorage.setItem(SecureTokenManager.TOKEN_KEY, accessToken);
    } catch (error) {
      console.error("Failed to store tokens securely:", error);
    }
  }

  static getToken(): string | null {
    const localToken = localStorage.getItem(SecureTokenManager.TOKEN_KEY);

    if (localToken && SecureTokenManager.isTokenValid(localToken)) {
      return localToken;
    }

    SecureTokenManager.clearTokens();
    return null;
  }

  private static isTokenValid(token: string): boolean {
    try {
      const payload = jwtDecode<CustomJwtPayload>(token);
      const expiry = payload.exp * 1000;
      const now = Date.now();

      return expiry > now + 60000;
    } catch (error) {
      return false;
    }
  }

  static async refreshIfNeeded(): Promise<boolean> {
    const currentToken = SecureTokenManager.getToken();

    if (!currentToken) {
      return false;
    }

    try {
      const response = (await refreshTokenIfNeeded()) as ApiResponse<TokenRefreshResponse>;
      const data: TokenRefreshResponse = response.data;

      if (data.tokenRefreshed) {
        if (data.access_token) {
          SecureTokenManager.storeTokens(data.access_token);
        }
        return true;
      } else {
        return true;
      }
    } catch (error) {
      return false;
    }
  }

  static isAuthenticated(): boolean {
    return !!SecureTokenManager.getToken();
  }

  static clearTokens(): void {
    localStorage.removeItem(SecureTokenManager.TOKEN_KEY);
  }

  static getAccessToken(): string {
    const token = SecureTokenManager.getToken();
    return token || "";
  }
}
